import { useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { MultipleAttachments } from 'components/common/form';

import type { Attachments } from 'shared-types';
import type { GetAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: GetAchievementsRes['achievements'][0];
  updateAchievement: () => Promise<void>;
}

const AchievementEditAttachmentSelf = ({
  achievement,
  updateAchievement
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  const uploader = useRef<() => Promise<Attachments>>(undefined);

  const handleSubmit = async () => {
    try {
      const attachments = await uploader.current();

      await api.put(`/admin/achievements/${achievement.id}/completions`, {
        attachmentOnly: true,
        updatedCompletion: { attachments },
        userId: user.id
      });

      await updateAchievement();

      dispatch.addNotice({
        text: messages.commonEditAttachmentsSuccess,
        type: 'success'
      });
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const completion = achievement.completions?.[0];

  const label = completion?.attachments?.length
    ? messages.commonEditAttachments
    : messages.commonAddAttachment;

  return achievement.certificateAllowAdding ? (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonProps={{ isRounded: true }}
      buttonText={label}
      initialValues={{ attachments: completion?.attachments || [] }}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalTitle={label}
      modalWidth="medium"
      onSubmit={handleSubmit}
      stopPropagation={true}
    >
      {() => (
        <MultipleAttachments
          getUploadHandler={(fn) => (uploader.current = fn)}
          required={achievement.certificateRequiredFromUser}
        />
      )}
    </ModalButtonWithForm>
  ) : null;
};

export default AchievementEditAttachmentSelf;
